import React, { useState, useEffect } from 'react';
import { Calendar, Star, Shield, Info, TrendingUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ReliabilityBadge } from '@/components/reliability/ReliabilityBadge';
import { BACKEND_URL } from '@/lib/backendUrl';


/**
 * CancellationPolicyCard Component
 * Displays HOTEL'S CUSTOM cancellation policy (BASE tiers without reliability adjustment)
 * Shows what refund %s are available based on booking timing
 * Optionally shows reliability bonus note if guest is logged in
 */
export function CancellationPolicyCard({ hotelId, hotel, guestEmail, compact = false }) {
  const [hotelPolicy, setHotelPolicy] = useState(null);
  const [reliabilityTier, setReliabilityTier] = useState(null);
  const [reliabilityAdjustment, setReliabilityAdjustment] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      const promises = [];
      
      // If hotel object with policy is provided, use it directly
      if (hotel && hotel.cancellation_tiers) {
        setHotelPolicy({
          success: true,
          policy: hotel.cancellation_tiers,
          is_custom: true
        });
      } else if (hotelId) {
        promises.push(fetchHotelPolicy());
      }
      
      if (guestEmail) {
        promises.push(fetchGuestReliability());
      }
      
      await Promise.all(promises);
      setLoading(false);
    };
    
    loadData();
  }, [hotelId, guestEmail, hotel]);

  const fetchHotelPolicy = async () => {
    try {
      const url = `${BACKEND_URL}/api/hotels/${hotelId}/cancellation-policy`;
      const response = await fetch(url);
      
      if (!response.ok) throw new Error(`Failed to fetch hotel policy: ${response.status}`);
      
      const data = await response.json();
      setHotelPolicy(data);
    } catch (err) {
      console.error('Error fetching hotel cancellation policy:', err);
      setError(err.message);
      // Fallback to default system policy
      setHotelPolicy({
        is_custom: false,
        policy: [
          { name: 'Early Bird', days_min: 30, days_max: null, base_refund_pct: 50 },
          { name: 'Advance', days_min: 14, days_max: 29, base_refund_pct: 75 },
          { name: 'Standard', days_min: 3, days_max: 13, base_refund_pct: 90 },
          { name: 'Last Minute', days_min: 0, days_max: 2, base_refund_pct: 100 }
        ]
      });
    }
  };

  const fetchGuestReliability = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/reliability/cancellation-policy/${encodeURIComponent(guestEmail)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setReliabilityTier(data.reliability_tier);
        setReliabilityAdjustment(data.policy?.adjustment || 0);
      }
    } catch (err) {
      console.error('Error fetching guest reliability:', err);
    }
  };

  if (loading) {
    return (
      <Card data-testid="cancellation-policy-loading">
        <CardContent className="py-6">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && !hotelPolicy) {
    return (
      <Alert>
        <Info className="w-4 h-4" />
        <AlertDescription>
          Unable to load cancellation policy. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const policy = hotelPolicy?.policy || [];
  const isCustom = hotelPolicy?.is_custom || false;

  // Compact version for inline display
  if (compact) {
    return (
      <div 
        className="space-y-2 p-4 bg-muted/30 rounded-lg border"
        data-testid="cancellation-policy-compact"
      >
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-primary" />
          <p className="text-sm font-semibold">Cancellation Policy</p>
          {isCustom && (
            <Badge variant="secondary" className="text-xs">
              Custom
            </Badge>
          )}
        </div>
        
        {/* Show all tiers */}
        <div className="space-y-1.5">
          {policy && policy.length > 0 ? (
            policy.map((tier, idx) => {
              const dayRange = tier.days_max 
                ? `${tier.days_min}-${tier.days_max} days`
                : `${tier.days_min}+ days`;
              
              return (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    <strong>{tier.name}</strong> ({dayRange})
                  </span>
                  <span className="font-bold text-primary">
                    {tier.base_refund_pct}% refund
                  </span>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-muted-foreground">
              Standard flexible cancellation terms apply.
            </p>
          )}
        </div>

        {guestEmail && reliabilityTier && reliabilityAdjustment !== 0 && (
          <p className="text-xs text-primary mt-2 pt-2 border-t">
            ✨ <strong>Your {reliabilityTier} status: {reliabilityAdjustment > 0 ? '+' : ''}{reliabilityAdjustment}% bonus on these refunds!</strong>
          </p>
        )}
        
        <Alert className="bg-blue-50/50 border-blue-200 mt-2">
          <Info className="w-3.5 h-3.5 text-blue-600" />
          <AlertDescription className="text-xs text-blue-800">
            🔒 Your refund % will be locked when you book based on your booking timing{guestEmail ? ' and reliability status' : ''}.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Full card version
  return (
    <Card data-testid="cancellation-policy-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Cancellation Policy
          </CardTitle>
          {isCustom && (
            <Badge variant="secondary" className="text-xs">
              Hotel Custom Policy
            </Badge>
          )}
        </div>
        <CardDescription className="text-sm mt-2">
          {isCustom 
            ? 'This hotel has defined custom cancellation terms.'
            : 'Standard flexible cancellation terms apply.'
          }
          {guestEmail && reliabilityTier && (
            <> Your <strong>{reliabilityTier}</strong> status gives you {reliabilityAdjustment > 0 ? '+' : ''}{reliabilityAdjustment}% bonus!</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Hotel's Custom Policy Tiers */}
        <div className="space-y-2">
          {policy.map((tier, idx) => {
            const dayRange = tier.days_max 
              ? `${tier.days_min}-${tier.days_max} days`
              : `${tier.days_min}+ days`;
            
            const priceColor = tier.price_adjustment_pct < 0 
              ? 'text-emerald-600' 
              : tier.price_adjustment_pct > 0 
                ? 'text-amber-600' 
                : 'text-muted-foreground';
            
            return (
              <div 
                key={idx}
                className="flex items-center justify-between p-2.5 bg-muted/40 rounded-md hover:bg-muted/60 transition-colors"
                data-testid={`policy-tier-${idx}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{tier.name}</span>
                    <span className="text-xs text-muted-foreground">({dayRange})</span>
                  </div>
                  {tier.price_adjustment_pct !== 0 && (
                    <p className={`text-xs ${priceColor} mt-0.5`}>
                      {tier.price_adjustment_pct > 0 ? '+' : ''}{tier.price_adjustment_pct}% price adjustment
                    </p>
                  )}
                </div>
                <Badge 
                  variant="secondary" 
                  className="font-bold"
                >
                  {tier.base_refund_pct}% refund
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Reliability Bonus Notice */}
        {guestEmail && reliabilityTier === 'Excellent' && reliabilityAdjustment > 0 && (
          <Alert className="bg-yellow-500/5 border-yellow-500/20">
            <Star className="w-4 h-4 text-yellow-600" />
            <AlertDescription className="text-xs">
              <strong>Excellent Guest Bonus:</strong> You get +{reliabilityAdjustment}% better refund terms on top of the base policy!
            </AlertDescription>
          </Alert>
        )}

        {guestEmail && reliabilityTier === 'Standard' && reliabilityAdjustment < 0 && (
          <Alert className="bg-gray-500/5 border-gray-500/20">
            <Shield className="w-4 h-4 text-gray-600" />
            <AlertDescription className="text-xs">
              <strong>Standard Terms:</strong> Complete more stays without canceling to unlock Excellent status (+25% refund bonus)!
            </AlertDescription>
          </Alert>
        )}

        {/* Info notice */}
        <Alert className="bg-blue-50/50 border-blue-200">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-xs text-blue-800">
            🔒 <strong>Your refund % will be locked</strong> when you book based on your booking timing and reliability status.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

export default CancellationPolicyCard;
